/**
 * Staged File Key Utilities
 *
 * Centralizes the key format logic for staged files to ensure consistency
 * across the application. This utility handles the various key formats used
 * when storing and looking up staged files.
 *
 * KEY FORMATS:
 * - Full key: "formId-subformId-fieldId" (e.g., "17-8-field_123")
 * - Legacy key: "subformId-fieldId" (e.g., "8-field_123")
 * - Lookup key: "subformId_fieldId" (e.g., "8_field_123")
 *
 * USAGE SCENARIOS:
 * 1. New request draft: formId is available, full key is used
 * 2. Send-back draft: formId is available, full key is used
 * 3. Legacy drafts: formId may be missing, legacy key is used
 *
 * @module stagedFileKeyUtils
 */

/**
 * Creates a staged file key from component options.
 * Used when uploading a new file to track it by field.
 *
 * @param {Object} options - Upload options
 * @param {number|string} options.formId - Form ID (optional)
 * @param {number|string} options.subformId - Subform ID (required)
 * @param {string} options.fieldId - Field ID (required)
 * @returns {string|null} The key in format "formId-subformId-fieldId" or "subformId-fieldId"
 *
 * @example
 * // With formId
 * createStagedFileKey({ formId: 17, subformId: 8, fieldId: 'field_123' })
 * // Returns: "17-8-field_123"
 *
 * @example
 * // Without formId
 * createStagedFileKey({ subformId: 8, fieldId: 'field_123' })
 * // Returns: "8-field_123"
 */
export const createStagedFileKey = ({ formId, subformId, fieldId }) => {
  if (!subformId || !fieldId) return null;

  return formId
    ? `${formId}-${subformId}-${fieldId}`
    : `${subformId}-${fieldId}`;
};

/**
 * Looks up staged files using both key formats.
 * Tries the full key first, falls back to legacy key.
 *
 * @param {Object} stagedFilesMap - Map of staged files keyed by various formats
 * @param {number|string} formId - Form ID
 * @param {number|string} subformId - Subform ID
 * @param {string} fieldId - Field ID
 * @returns {Array} Array of staged files for this field (empty if none found)
 *
 * @example
 * // Handles both key formats automatically
 * const files = lookupStagedFiles(draftStagedFiles, 17, 8, 'field_123');
 */
export const lookupStagedFiles = (
  stagedFilesMap,
  formId,
  subformId,
  fieldId
) => {
  if (!stagedFilesMap || typeof stagedFilesMap !== 'object') {
    return [];
  }

  const key = createStagedFileKey({ formId, subformId, fieldId });

  // Try full key first (with formId), then legacy key
  return stagedFilesMap[key] || [];
};

/**
 * Normalizes a staged file key to the lookup format used by useFileAttachments.
 * Converts from storage format to lookup format.
 *
 * Storage format: "formId-subformId-fieldId" or "subformId-fieldId"
 * Lookup format: "subformId_fieldId"
 *
 * @param {string} key - Key in storage format
 * @param {number|string|null} currentFormId - Current form ID to filter by (optional)
 * @returns {string|null} Normalized key or null if filtered out
 *
 * @example
 * normalizeKeyForLookup("17-8-field_123", 17)
 * // Returns: "8_field_123"
 *
 * normalizeKeyForLookup("17-8-field_123", 20)
 * // Returns: null (formId doesn't match)
 *
 * normalizeKeyForLookup("8-field_123", null)
 * // Returns: "8_field_123"
 */
export const normalizeKeyForLookup = (key, currentFormId = null) => {
  const parts = String(key).split('-');

  if (parts.length >= 3) {
    // Full key format: formId-subformId-fieldId
    const [keyFormId, keySubformId, ...fieldParts] = parts;

    // Filter by formId if provided and doesn't match
    if (currentFormId && String(keyFormId) !== String(currentFormId)) {
      return null;
    }

    const fieldId = fieldParts.join('-');
    return `${keySubformId}_${fieldId}`;
  }

  if (parts.length === 2) {
    // Legacy key format: subformId-fieldId (no formId)
    const [keySubformId, ...fieldParts] = parts;
    const fieldId = fieldParts.join('-');
    return `${keySubformId}_${fieldId}`;
  }

  return null;
};

/**
 * Normalizes a file ID to a consistent string format.
 * Handles both numeric and string IDs.
 *
 * @param {number|string|null|undefined} id - File ID to normalize
 * @returns {string|null} Normalized ID as string, or null if invalid
 *
 * @example
 * normalizeFileId(123)     // Returns: "123"
 * normalizeFileId("123")   // Returns: "123"
 * normalizeFileId(null)    // Returns: null
 * normalizeFileId(undefined) // Returns: null
 */
export const normalizeFileId = (id) => {
  if (id === null || id === undefined) return null;
  return id;
};

/**
 * Creates a lookup key in the format used by useFileAttachments hooks.
 *
 * @param {number|string} subformId - Subform ID
 * @param {string} fieldId - Field ID
 * @returns {string} Lookup key in format "subformId_fieldId"
 *
 * @example
 * createLookupKey(8, 'field_123')
 * // Returns: "8_field_123"
 */
export const createLookupKey = (subformId, fieldId) => {
  return `${String(subformId)}_${fieldId}`;
};

export default {
  createStagedFileKey,
  lookupStagedFiles,
  normalizeKeyForLookup,
  normalizeFileId,
  createLookupKey,
};
